import {
  ConversationHistory,
  StoredMessage,
  ConversationRecord,
  ConversationSummary,
  Message,
  BackendConversationsPage,
  BackendConversation,
  BackendConversationDetail,
} from '../types/chat.types';
import { apiClient } from '../../../lib/apiClient';
import { STORAGE_KEYS } from '../../../config/appConfig';

const STORAGE_KEY = STORAGE_KEYS.chatHistory;

const isBrowser = typeof window !== 'undefined';

let inMemoryHistory: ConversationHistory = {};

const readHistory = (): ConversationHistory => {
  if (!isBrowser) {
    return { ...inMemoryHistory };
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      inMemoryHistory = {};
      return {};
    }

    const parsed = JSON.parse(raw) as ConversationHistory;
    inMemoryHistory = parsed;
    return { ...parsed };
  } catch (error) {
    console.error('[chatHistoryService] Error reading history, clearing local cache:', error);
    inMemoryHistory = {};
    if (isBrowser) {
      window.localStorage.removeItem(STORAGE_KEY);
    }
    return {};
  }
};

const writeHistory = (history: ConversationHistory) => {
  inMemoryHistory = { ...history };
  if (!isBrowser) {
    return;
  }

  try {
    if (Object.keys(history).length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return;
    }

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    // localStorage is a local cache, not the source of truth (the backend is).
    // Letting this throw (e.g. QuotaExceededError once the blob nears ~5MB)
    // propagates into ChatContext.sendMessage's try/catch and surfaces a
    // "message failed" banner over a message that actually sent fine — see
    // .claude-notes/FRONTEND_PENDIENTES.md §2.3. Degrade silently instead:
    // the current session keeps working from React state and in-memory
    // history; only the on-disk cache misses this write, so a page reload
    // may show slightly stale local history until the next backend sync.
    console.error('[chatHistoryService] Failed to persist history to localStorage (local cache only, chat continues normally):', error);
  }
};

const computeUpdatedAt = (record: ConversationRecord): string => {
  if (!record.messages.length) {
    return record.createdAt;
  }
  const last = record.messages[record.messages.length - 1];
  return last.createdAt;
};

const toSummary = (record: ConversationRecord): ConversationSummary => ({
  conversationId: record.conversationId,
  name: record.name,
  createdAt: record.createdAt,
  updatedAt: computeUpdatedAt(record),
  messageCount: record.messages.length,
});

const resolveRole = (value?: string): StoredMessage['role'] => {
  if (value === 'user') {
    return 'user';
  }
  return 'assistant';
};

const normalizeStoredMessage = (
  message: Partial<StoredMessage> & { messageId?: string; sender?: string },
  indexSeed: number,
): StoredMessage => ({
  id:
    message.id ||
    message.messageId ||
    `local_${indexSeed}_${Math.random().toString(36).slice(2, 10)}`,
  createdAt: message.createdAt || new Date().toISOString(),
  role: resolveRole(message.role ?? message.sender),
  content: message.content ?? '',
  isLiked: message.isLiked ?? false,
});

const convertToStoredMessages = (messages: Message[]): StoredMessage[] =>
  messages.map((message, index) =>
    normalizeStoredMessage(
      {
        id: message.id,
        createdAt: message.createdAt,
        role: message.role,
        content: message.content,
        isLiked: message.isLiked ?? false,
      },
      index,
    ),
  );

const mapBackendSummaryToRecord = (backend: BackendConversation): ConversationRecord => ({
  conversationId: backend.conversation_id,
  name: backend.name || 'New conversation',
  createdAt: backend.created_at,
  messages: [],
  lastMessageId: undefined,
});

const mapBackendDetailToRecord = (detail: BackendConversationDetail): ConversationRecord => {
  const rawMessages = Array.isArray(detail.messages) ? detail.messages : [];

  const messages: StoredMessage[] = rawMessages.map((msg) => ({
    id: msg.message_id,
    createdAt: msg.created_at,
    role: msg.role === 'user' ? 'user' : 'assistant',
    content: msg.content,
    isLiked: msg.is_liked ?? false,
  }));

  const lastAssistantMessageId = [...messages]
    .reverse()
    .find((message) => message.role === 'assistant')?.id;

  return {
    conversationId: detail.conversation_id,
    name: detail.name || 'New conversation',
    createdAt: detail.created_at,
    messages,
    lastMessageId: lastAssistantMessageId,
  };
};

/**
 * Merges an incoming backend record with the existing local one (if any).
 * Prefers the local name when it has been customised (auto-generated from
 * the first message or explicitly renamed by the user) — the backend stores
 * "New conversation" until a rename is explicitly sent, so it must not
 * overwrite a locally-set title. Shared by both `syncWithBackendHistory`
 * (replace) and `mergeBackendHistory` (additive) — they differ only in how
 * they seed the starting map, not in this per-record logic.
 */
const mergeRecord = (
  incoming: ConversationRecord,
  existing: ConversationRecord | undefined,
): ConversationRecord => {
  if (!existing) {
    return { ...incoming, messages: incoming.messages.map((message) => ({ ...message })) };
  }

  return {
    ...incoming,
    name: existing.name !== 'New conversation' ? existing.name : incoming.name,
    messages: existing.messages.length
      ? existing.messages.map((message) => ({ ...message }))
      : incoming.messages.map((message) => ({ ...message })),
    lastMessageId: existing.lastMessageId ?? incoming.lastMessageId,
    ...(existing.tokenCount !== undefined && {
      tokenCount: existing.tokenCount,
      isExhausted: existing.isExhausted ?? false,
    }),
  };
};

const fetchConversationsFromBackend = async (
  accessToken: string,
  { limit, cursor }: { limit?: number; cursor?: string } = {},
): Promise<{ history: ConversationHistory; nextCursor: string | null }> => {
  try {
    const params = new URLSearchParams();
    if (limit !== undefined) {
      params.set('limit', String(limit));
    }
    if (cursor) {
      params.set('cursor', cursor);
    }
    const query = params.toString();
    const endpoint = `/db/user-conversations${query ? `?${query}` : ''}`;

    const data = await apiClient<BackendConversationsPage>(endpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const history: ConversationHistory = {};

    data.items.forEach((backendConversation: BackendConversation) => {
      if (!backendConversation.conversation_id) {
        console.warn('[chatHistoryService] Skipping conversation without an id', backendConversation);
        return;
      }

      history[backendConversation.conversation_id] = mapBackendSummaryToRecord(backendConversation);
    });

    return { history, nextCursor: data.next_cursor };
  } catch (error) {
    console.error('[chatHistoryService] Error fetching conversations from backend:', error);
    throw error;
  }
};

const fetchConversationById = async (
  conversationId: string,
  accessToken: string
): Promise<ConversationRecord> => {
  try {
    const data = await apiClient<BackendConversationDetail>(
      `/db/conversation/${conversationId}`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    if (!Array.isArray(data.messages)) {
      console.warn('[chatHistoryService] Conversation detail does not include a messages array', conversationId);
      data.messages = [];
    }

    return mapBackendDetailToRecord(data);
  } catch (error) {
    console.error('[chatHistoryService] Error fetching conversation by ID:', conversationId, error);
    throw error;
  }
};

const renameConversationOnBackend = async (
  conversationId: string,
  name: string,
  accessToken: string,
): Promise<BackendConversation> => {
  const response = await apiClient<BackendConversation>(`/db/conversations/${conversationId}/name`, {
    method: 'PATCH',
    headers: { Authorization: `Bearer ${accessToken}` },
    body: { new_name: name },
  });

  return response;
};

const markConversationUnsavedOnBackend = async (
  conversationId: string,
  accessToken: string,
): Promise<BackendConversation> => {
  const response = await apiClient<BackendConversation>(
    `/db/conversations/${conversationId}/unsaved`,
    {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );

  return response;
};

export const chatHistoryService = {
  fetchFromBackend: fetchConversationsFromBackend,
  fetchConversationById: fetchConversationById,
  renameConversationRemote: renameConversationOnBackend,
  deleteConversationRemote: markConversationUnsavedOnBackend,

  // Replace semantics: the incoming page is treated as the full truth (used
  // for the first page of history on load). Anything locally cached that
  // isn't in `backendHistory` is dropped.
  syncWithBackendHistory: (backendHistory: ConversationHistory) => {
    const currentHistory = readHistory();
    const nextHistory: ConversationHistory = {};

    Object.values(backendHistory).forEach((record) => {
      nextHistory[record.conversationId] = mergeRecord(record, currentHistory[record.conversationId]);
    });

    writeHistory(nextHistory);
    return nextHistory;
  },

  // Additive semantics: used for "Load more" pages — folds the incoming page
  // into the current history without dropping conversations already loaded
  // from earlier pages.
  mergeBackendHistory: (backendHistory: ConversationHistory) => {
    const currentHistory = readHistory();
    const nextHistory: ConversationHistory = { ...currentHistory };

    Object.values(backendHistory).forEach((record) => {
      nextHistory[record.conversationId] = mergeRecord(record, currentHistory[record.conversationId]);
    });

    writeHistory(nextHistory);
    return nextHistory;
  },

  listConversations: (): ConversationSummary[] => {
    const history = readHistory();
    return Object.values(history)
      .map(toSummary)
      .sort((a, b) => (a.updatedAt > b.updatedAt ? -1 : 1));
  },

  getConversation: (conversationId: string): ConversationRecord | null => {
    const history = readHistory();
    return history[conversationId] ? { ...history[conversationId] } : null;
  },

  saveConversation: (record: ConversationRecord): ConversationRecord => {
    const history = readHistory();
    const messages = record.messages.map((message, index) =>
      normalizeStoredMessage(message, index),
    );

    const conversation: ConversationRecord = {
      conversationId: record.conversationId,
      createdAt: record.createdAt || new Date().toISOString(),
      name: record.name,
      messages,
      lastMessageId: record.lastMessageId,
      ...(record.tokenCount !== undefined && {
        tokenCount: record.tokenCount,
        isExhausted: record.isExhausted ?? false,
      }),
    };

    history[conversation.conversationId] = conversation;
    writeHistory(history);
    return conversation;
  },

  createConversation: (input: {
    conversationId?: string;
    name?: string;
    createdAt?: string;
    messages?: Message[];
    lastMessageId?: string;
    tokenCount?: number;
    isExhausted?: boolean;
  }): ConversationRecord => {
    const conversationId =
      input.conversationId ||
      (isBrowser && window.crypto?.randomUUID
        ? window.crypto.randomUUID()
        : `conversation_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`);

    const messages = input.messages ? convertToStoredMessages(input.messages) : [];

    const record: ConversationRecord = {
      conversationId,
      name: input.name || 'New conversation',
      createdAt: input.createdAt || new Date().toISOString(),
      messages,
      lastMessageId: input.lastMessageId,
      ...(input.tokenCount !== undefined && {
        tokenCount: input.tokenCount,
        isExhausted: input.isExhausted ?? false,
      }),
    };

    return chatHistoryService.saveConversation(record);
  },

  appendMessage: (conversationId: string, message: Message): ConversationRecord | null => {
    const history = readHistory();
    const existing = history[conversationId];
    if (!existing) {
      return null;
    }

    const normalized = convertToStoredMessages([message])[0];
    const updated: ConversationRecord = {
      ...existing,
      messages: [...existing.messages, normalized],
    };

    history[conversationId] = updated;
    writeHistory(history);
    return updated;
  },

  deleteConversation: (conversationId: string): boolean => {
    const history = readHistory();
    if (!history[conversationId]) {
      return false;
    }

    delete history[conversationId];
    writeHistory(history);
    return true;
  },

  renameConversation: (conversationId: string, name: string): ConversationRecord | null => {
    const history = readHistory();
    const existing = history[conversationId];
    if (!existing) {
      return null;
    }

    const updated = { ...existing, name };
    history[conversationId] = updated;
    writeHistory(history);
    return updated;
  },

  replaceMessages: (conversationId: string, messages: Message[]): ConversationRecord | null => {
    const history = readHistory();
    const existing = history[conversationId];
    if (!existing) {
      return null;
    }

    const converted = convertToStoredMessages(messages);
    const updated = { ...existing, messages: converted };
    history[conversationId] = updated;
    writeHistory(history);
    return updated;
  },

  clear: () => {
    writeHistory({});
  },
};

export type ChatHistoryService = typeof chatHistoryService;
